import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditMultiSelectField } from '../../../EditMultiSelectField';
import {
  type AllPreferredIndustriesData,
  GET_ACCOUNT_DEAL_PREFERENCES_AND_PREFERENCES,
  GET_ALL_PREFERRED_INDUSTRIES,
  type InvestmentPreferencesData
} from '../queries/accountPreferencesQuery';

const UPDATE_ACCOUNT_INDUSTRIES = gql`
  mutation UpdateAccountIndustries($id: ID!, $values: AccountInput!) {
    updateAccount(id: $id, values: $values) {
      ok
      account {
        id
        name
        preferredIndustryIds {
          id
          name
          isActive
        }
      }
    }
  }
`;

interface EditPreferredIndustriesDialogProps {
  accountId: string;
  initialValue: InvestmentPreferencesData;
  isOpen: boolean;
  onChange: (data: InvestmentPreferencesData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: InvestmentPreferencesData;
}

export function EditPreferredIndustriesDialog({
  accountId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditPreferredIndustriesDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  // Fetch all available industries
  const { data: allIndustriesData, loading: loadingIndustries } =
    useQuery<AllPreferredIndustriesData>(GET_ALL_PREFERRED_INDUSTRIES, {
      fetchPolicy: 'cache-first'
    });

  const [updateAccount] = useMutation(UPDATE_ACCOUNT_INDUSTRIES, {
    refetchQueries: [
      {
        query: GET_ACCOUNT_DEAL_PREFERENCES_AND_PREFERENCES,
        variables: { accountId }
      }
    ]
  });

  const options = useMemo((): { label: string; value: string }[] => {
    if (!allIndustriesData?.preferredIndustries) return [];
    return allIndustriesData.preferredIndustries
      .filter(item => item.isActive)
      .map(item => ({
        label: item.name,
        value: item.id
      }));
  }, [allIndustriesData]);

  // Get selected IDs from value
  const selectedIds = useMemo(
    (): string[] => value.preferredIndustries.map(i => i.id),
    [value.preferredIndustries]
  );

  // Check if dirty by comparing IDs
  const initialIds = useMemo(
    (): string[] => initialValue.preferredIndustries.map(i => i.id).sort(),
    [initialValue.preferredIndustries]
  );
  const currentIds = useMemo(
    (): string[] => [...selectedIds].sort(),
    [selectedIds]
  );
  const isDirty = JSON.stringify(currentIds) !== JSON.stringify(initialIds);

  const handleChange = (ids: string[]) => {
    // Convert IDs back to PreferenceItem objects
    const items = ids.map(id => {
      // Check if it's already in value
      const existing = value.preferredIndustries.find(i => i.id === id);
      if (existing) return existing;
      // Otherwise find from options
      const option = options.find(o => o.value === id);
      return { id, name: option?.label ?? '' };
    });
    onChange({ ...value, preferredIndustries: items });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateAccount({
        variables: {
          id: accountId,
          values: {
            preferredIndustryIds: selectedIds
          }
        }
      });

      const data = response.data as {
        updateAccount?: { ok?: boolean };
      };

      if (data.updateAccount?.ok === false) {
        toast.error('Failed to save preferred industries');
      } else {
        toast.success('Preferred industries saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving preferred industries:', error);
      toast.error('Failed to save preferred industries');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Preferred Industries"
    >
      <EditMultiSelectField
        label="Preferred Industries"
        onChange={handleChange}
        options={options}
        placeholder={loadingIndustries ? 'Loading...' : 'Select industries...'}
        values={selectedIds}
      />
    </EditDialog>
  );
}
